"use client";
import { useState } from "react";
import { useRouter } from "next/navigation";
import { BoardCard } from "@/lib/data";

interface Props {
  card: BoardCard;
  onClose: () => void;
}

const statusOptions = [
  { value: "backlog",     label: "Backlog" },
  { value: "planning",   label: "Planning" },
  { value: "brainstorm", label: "Brainstorm" },
  { value: "active",     label: "In Progress" },
  { value: "waiting",    label: "Waiting" },
  { value: "blocked",    label: "Blocked" },
  { value: "done",       label: "Done" },
  { value: "wont_do",    label: "Won't Do" },
];

const assigneeOptions = [
  { value: null, label: "Unassigned" },
  { value: "robby", label: "Robby" },
  { value: "weston", label: "Weston" },
];

export default function EditCardModal({ card, onClose }: Props) {
  const router = useRouter();
  const [name, setName] = useState(card.name);
  const [description, setDescription] = useState(card.description);
  const [status, setStatus] = useState(card.status);
  const [notes, setNotes] = useState(card.notes);
  const [assignedTo, setAssignedTo] = useState<"robby" | "weston" | null>(card.assignedTo ?? null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState("");

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) { setError("Name is required."); return; }
    setSaving(true);
    setError("");
    try {
      const res = await fetch("/api/projects", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: card.id, action: "update", name, description, status, notes, assignedTo }),
      });
      if (!res.ok) throw new Error("Save failed");
      router.refresh();
      onClose();
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    setDeleting(true);
    try {
      const res = await fetch("/api/projects", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: card.id }),
      });
      if (!res.ok) throw new Error("Delete failed");
      router.refresh();
      onClose();
    } catch {
      setError("Delete failed. Please try again.");
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <div className="fixed inset-0 bg-[#2c2416]/30 backdrop-blur-[2px] z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
        <div className="bg-[#faf8f4] border border-[#e4ddd0] rounded-xl shadow-xl w-full max-w-lg">

          {/* Header */}
          <div className="flex items-center justify-between px-7 py-5 border-b border-[#e8e2d8]">
            <div className="flex items-center gap-2">
              <span>{card.type === "project" ? "📁" : "✅"}</span>
              <div>
                <div className="font-mono text-[10px] font-semibold text-[#a0906e] mb-1">{card.ticketNumber}</div>
                <h2 className="font-serif text-[20px] text-[#2c2416]">Edit {card.type === "project" ? "Project" : "Task"}</h2>
              </div>
            </div>
            <button onClick={onClose} className="text-[#b0a090] hover:text-[#6b5c48] text-xl leading-none transition-colors">×</button>
          </div>

          <form onSubmit={handleSave} className="px-7 py-6 flex flex-col gap-5">

            {/* Name */}
            <div>
              <label className="block text-[11px] tracking-[0.1em] uppercase text-[#8a7a68] font-semibold mb-2">Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-white border border-[#e4ddd0] rounded-lg px-4 py-2.5 text-[14px] text-[#2c2416] placeholder-[#c0b8b0] focus:outline-none focus:border-[#b5622a] transition-colors"
                autoFocus
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-[11px] tracking-[0.1em] uppercase text-[#8a7a68] font-semibold mb-2">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                className="w-full bg-white border border-[#e4ddd0] rounded-lg px-4 py-2.5 text-[14px] text-[#2c2416] placeholder-[#c0b8b0] focus:outline-none focus:border-[#b5622a] transition-colors resize-none"
              />
            </div>

            {/* Status */}
            <div>
              <label className="block text-[11px] tracking-[0.1em] uppercase text-[#8a7a68] font-semibold mb-2">Column / Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as BoardCard["status"])}
                className="w-full bg-white border border-[#e4ddd0] rounded-lg px-4 py-2.5 text-[14px] text-[#2c2416] focus:outline-none focus:border-[#b5622a] transition-colors"
              >
                {statusOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-[11px] tracking-[0.1em] uppercase text-[#8a7a68] font-semibold mb-2">
                Notes <span className="font-normal normal-case tracking-normal text-[#b0a090]">(optional)</span>
              </label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full bg-white border border-[#e4ddd0] rounded-lg px-4 py-2.5 text-[14px] text-[#2c2416] placeholder-[#c0b8b0] focus:outline-none focus:border-[#b5622a] transition-colors"
              />
            </div>

            {/* Assigned To */}
            <div>
              <label className="block text-[11px] tracking-[0.1em] uppercase text-[#8a7a68] font-semibold mb-2">Assigned To</label>
              <select
                value={assignedTo ?? ""}
                onChange={(e) => setAssignedTo(e.target.value === "" ? null : (e.target.value as "robby" | "weston"))}
                className="w-full bg-white border border-[#e4ddd0] rounded-lg px-4 py-2.5 text-[14px] text-[#2c2416] focus:outline-none focus:border-[#b5622a] transition-colors"
              >
                {assigneeOptions.map((opt) => (
                  <option key={opt.value ?? "null"} value={opt.value ?? ""}>{opt.label}</option>
                ))}
              </select>
            </div>

            {error && <p className="text-[12px] text-red-500">{error}</p>}

            {/* Actions */}
            <div className="flex items-center justify-between pt-1">
              {/* Delete */}
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className={`text-[12px] transition-colors ${
                  confirmDelete
                    ? "text-red-500 hover:text-red-700 font-medium"
                    : "text-[#c0b8b0] hover:text-red-400"
                }`}
              >
                {deleting ? "Deleting…" : confirmDelete ? "Click again to confirm" : "Delete"}
              </button>

              <div className="flex gap-3">
                <button type="button" onClick={onClose} className="px-5 py-2 text-[13px] text-[#6b5c48] hover:text-[#2c2416] transition-colors">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2 bg-[#b5622a] text-white text-[13px] font-medium rounded-lg hover:bg-[#9e5424] transition-colors disabled:opacity-50"
                >
                  {saving ? "Saving…" : "Save Changes"}
                </button>
              </div>
            </div>

          </form>
        </div>
      </div>
    </>
  );
}
